import {useMutation, useQuery, useQueryClient} from '@github-ui/react-query'

import {createScribeArtifact, listScribeArtifacts} from './services'
import type {
  CreateScribeArtifactInput,
  ListScribeArtifactsOptions,
  ScribeArtifact,
  ScribeArtifactResourceId,
  ScribeArtifactResourceType,
} from './types'

export function scribeArtifactsQueryKey(
  resourceType: ScribeArtifactResourceType,
  resourceId: ScribeArtifactResourceId,
  type?: string,
): readonly unknown[] {
  return type
    ? (['scribe-artifacts', resourceType, String(resourceId), type] as const)
    : (['scribe-artifacts', resourceType, String(resourceId)] as const)
}

interface UseScribeArtifactsOptions extends Omit<ListScribeArtifactsOptions, 'signal'> {
  enabled?: boolean
}

export function useScribeArtifacts({resourceType, resourceId, type, enabled = true}: UseScribeArtifactsOptions) {
  return useQuery<ScribeArtifact[]>({
    queryKey: scribeArtifactsQueryKey(resourceType, resourceId, type),
    queryFn: ({signal}) => listScribeArtifacts({resourceType, resourceId, type, signal}),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

interface UseCreateScribeArtifactOptions {
  resourceType: ScribeArtifactResourceType
  resourceId: ScribeArtifactResourceId
}

type CreateInput = Pick<CreateScribeArtifactInput, 'name' | 'body' | 'type'>

export function useCreateScribeArtifact({resourceType, resourceId}: UseCreateScribeArtifactOptions) {
  const queryClient = useQueryClient()

  return useMutation<ScribeArtifact, Error, CreateInput>({
    mutationFn: input => createScribeArtifact({resourceType, resourceId, ...input}),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: scribeArtifactsQueryKey(resourceType, resourceId)})
    },
  })
}
